// Registry of pre-built reports.
//
// Each entry's `status` controls what the landing page shows:
//   - "available"    → tile is clickable, renders its `component`
//   - "coming-soon"  → visible but not ready to open yet
//
// When a report ships, flip its `status` to "available" and wire `component`.

import ForecastAccuracyReport from "./reports/ForecastAccuracyReport";
import LaborFlagsReport from "./reports/LaborFlagsReport";
import WeeklySimpleScheduleReport from "./reports/WeeklySimpleScheduleReport";
import WeeklyLaborScheduleDetailReport from "./reports/WeeklyLaborScheduleDetailReport";
import DailySchedulePreviewReport from "./reports/DailySchedulePreviewReport";
import LaborMetricGraphReport from "./reports/LaborMetricGraphReport";
import PunchInformationReport from "./reports/PunchInformationReport";
import LogEntriesByTypeReport from "./reports/LogEntriesByTypeReport";
import LaborSchedulingRosterReport from "./reports/LaborSchedulingRosterReport";
import DailyLaborScheduleSummaryReport from "./reports/DailyLaborScheduleSummaryReport";
import WeeklyLaborScheduleSummaryReport from "./reports/WeeklyLaborScheduleSummaryReport";
import DailyPunchAuditReport from "./reports/DailyPunchAuditReport";
import LaborSummaryReport from "./reports/LaborSummaryReport";
import PayrollReport from "./reports/PayrollReport";
import AutoscheduleEvaluationReport from "./reports/AutoscheduleEvaluationReport";
import AutoscheduleEvalRunsReport from "./reports/AutoscheduleEvalRunsReport";
import AutoscheduleRoleProfileReport from "./reports/AutoscheduleRoleProfileReport";
import AssistantQuestionsReport from "./reports/AssistantQuestionsReport";
import {
  DailyLaborRecapReport,
  WeeklyLaborRecapReport,
} from "./reports/LaborRecapReport";
import WeeklyLaborSummaryReport from "./reports/WeeklyLaborSummaryReport";
import FlashReport from "./reports/FlashReport";

export const REPORT_GROUPS = [
  { id: "schedule", label: "Schedule" },
  { id: "labor", label: "Labor & Punches" },
  { id: "sales", label: "Sales & Mix" },
  { id: "ops", label: "Operations" },
];

export const PREBUILT_REPORTS = [
  {
    id: "forecast-accuracy", name: "Forecast Accuracy",
    description: "Compare baseline and adjusted forecasts with actual sales by location and date.",
    group: "sales", status: "available", crossStore: true,
    requiredStoreFlag: "labor_model_enabled", requireActiveStore: true,
    component: ForecastAccuracyReport,
  },
  {
    id: "labor-flags",
    name: "Labor Flags",
    description: "Actual labor flags by location and type, with employee detail for a custom date range and CSV exports.",
    group: "labor",
    status: "available",
    crossStore: true,
    requiredStoreFlag: "labor_law_enabled",
    requireActiveStore: true,
    component: LaborFlagsReport,
  },
  // ───── Labor recaps ─────
  // First in the array so they lead the Labor & Punches group (tiles keep
  // registry order within a group).
  {
    id: "daily-labor-recap",
    name: "Daily Labor Recap",
    description:
      "The Daily Labor Recap email for any day: each store's labor grade, sales vs. forecast, hours vs. earned and scheduled, SPLH, and labor %, with company and district totals.",
    group: "labor",
    status: "available",
    // Covers every store the user manages in a company and keeps its own
    // company/store filters, so the printed header has no single store.
    crossStore: true,
    // The recap endpoint only covers open, fully onboarded labor-model
    // stores; requireActiveStore keeps the tile in line with it.
    requiredStoreFlag: "labor_model_enabled",
    requireActiveStore: true,
    component: DailyLaborRecapReport,
  },
  {
    id: "weekly-labor-recap",
    name: "Weekly Labor Recap",
    description:
      "The Weekly Labor Recap email for any business week: each store's sales vs. forecast, hours vs. earned and scheduled, SPLH, and labor %, with company and district totals.",
    group: "labor",
    status: "available",
    crossStore: true,
    requiredStoreFlag: "labor_model_enabled",
    requireActiveStore: true,
    component: WeeklyLaborRecapReport,
  },
  {
    id: "weekly-labor-summary",
    name: "Weekly Labor Summary",
    description:
      "The Weekly Labor Summary email for any business week: each store's daily labor grades for the week as planned and for the week before as planned vs. actual, with SPLH and a CSV download.",
    group: "labor",
    status: "available",
    crossStore: true,
    // The Weekly Labor Summary email goes to every open, onboarded
    // labor-model store, with no scheduling requirement.
    requiredStoreFlag: "labor_model_enabled",
    requireActiveStore: true,
    component: WeeklyLaborSummaryReport,
  },

  // ───── Available schedule reports ─────
  {
    id: "weekly-simple-schedule",
    name: "Weekly Simple Schedule",
    description:
      "Employee × day grid of shift times for the week. Print-friendly.",
    group: "schedule",
    status: "available",
    // Emailed as a CSV ("Email me this"); the schedule shown in its tooltip.
    emailSchedule: "Sent as a CSV at the start of each business week for the week ahead.",
    component: WeeklySimpleScheduleReport,
  },
  {
    id: "weekly-labor-schedule-detail",
    name: "Weekly Labor Schedule Detail",
    description:
      "Full week schedule grouped by employee, with role and station per shift.",
    group: "schedule",
    status: "available",
    // Emailed as a CSV ("Email me this"); the schedule shown in its tooltip.
    emailSchedule: "Sent as a CSV at the start of each business week for the week ahead.",
    component: WeeklyLaborScheduleDetailReport,
  },
  {
    id: "daily-schedule-preview",
    name: "Daily Schedule Preview",
    description:
      "Single-day roster grouped by job, with weather, sales forecast, SDLW, SDLY, and a week-to-date Approaching Overtime list.",
    group: "schedule",
    status: "available",
    component: DailySchedulePreviewReport,
  },
  {
    id: "labor-metric-graph",
    name: "Labor Metric Graph",
    description:
      "Intraday line graph comparing a sales metric against a labor-hours metric for a chosen day.",
    group: "labor",
    status: "available",
    component: LaborMetricGraphReport,
    // calculate_metric skips schedule-derived labor series for stores with
    // scheduling off, so those stores render a degraded graph. Soft gate:
    // they're hidden from the store picker while a scheduling-enabled store
    // exists, but an all-disabled account still opens the report for its
    // sales series (the deliberate carve-out in SCHEDULING_BACKED_REPORT_IDS
    // below).
    preferredStoreFlag: "scheduling_enabled",
  },
  {
    id: "autoschedule-evaluation",
    name: "Autoschedule Evaluation",
    description:
      "Store scorecard for schedule stability, labor fit, hours equity, role mix, keyholder coverage, and opening/closing consistency.",
    group: "schedule",
    status: "available",
    internalOnly: true,
    // Compares stores side by side and keeps its own store selection, so
    // the printed header has no single store to name.
    crossStore: true,
    component: AutoscheduleEvaluationReport,
  },
  {
    id: "autoschedule-role-profile",
    name: "Autoschedule Role Targets",
    description:
      "Internal view of how forecasted role-type labor is split into specific role targets throughout the day.",
    group: "schedule",
    status: "available",
    internalOnly: true,
    component: AutoscheduleRoleProfileReport,
  },
  {
    id: "autoschedule-eval-runs",
    name: "Autoschedule Eval Runs",
    description:
      "Internal comparison of persisted autoscheduler recommendations against actual schedules for the same store-week.",
    group: "schedule",
    status: "available",
    internalOnly: true,
    component: AutoscheduleEvalRunsReport,
  },

  // ───── Available labor and payroll reports ─────
  {
    id: "daily-labor-schedule-summary",
    name: "Daily Labor Schedule Summary",
    description:
      "Single-day rollups of scheduled vs. actual shifts, hours, rates, and wages — by employee, job, work center, and the day's total (with projected/actual sales and labor %).",
    group: "labor",
    status: "available",
    component: DailyLaborScheduleSummaryReport,
  },
  {
    id: "weekly-labor-schedule-summary",
    name: "Weekly Labor Schedule Summary",
    description:
      "Weekly rollups of scheduled vs. actual shifts, hours, rates, and wages — by employee, job, and work center — plus a per-day Day-of-Business grid with projected and actual sales and labor %.",
    group: "schedule",
    status: "available",
    component: WeeklyLaborScheduleSummaryReport,
  },
  {
    id: "labor-scheduling-roster",
    name: "Labor Scheduling Roster",
    description:
      "Scheduled vs. actual times per shift with minutes-early/late and $ variance, grouped by job.",
    group: "labor",
    status: "available",
    component: LaborSchedulingRosterReport,
  },
  {
    id: "punch-information",
    name: "Punch Information",
    description:
      "Per-shift clock-in/out by employee, job, and work center for a date range, with payroll-grade Reg / OT / DT / Hol hour splits at FLSA multipliers.",
    group: "labor",
    status: "available",
    // Emailed as a CSV ("Email me this"); the schedule shown in its tooltip.
    emailSchedule: "Sent as a CSV at the start of each business week for the week before.",
    component: PunchInformationReport,
    // This report's filters stack labels above their inputs; the shared
    // store picker follows suit so the filter row bottom-aligns.
    filterLabelStyle: "stacked",
  },
  {
    id: "labor-summary",
    name: "Labor Summary",
    description:
      "Per-day Reg / OT / DT / Hol hours and dollars with FLSA multipliers, meal break premiums, split shift premium hours, sales, and labor %.",
    group: "labor",
    status: "available",
    component: LaborSummaryReport,
  },
  {
    id: "daily-punch-audit",
    name: "Daily Punch Audit",
    description:
      "Single-day punches grouped by work center and job, with reg/OT/DT hours, paid and unpaid break detail, and pay rollups.",
    group: "labor",
    status: "available",
    // Emailed as a CSV ("Email me this"); the schedule shown in its tooltip.
    emailSchedule: "Sent as a CSV each morning for the day before.",
    component: DailyPunchAuditReport,
  },
  {
    id: "payroll-report",
    name: "Payroll Report",
    description:
      "Per-employee weekly rollup of Reg / OT / DT / Hol hours and pay plus meal break premiums and split shift premium hours.",
    group: "labor",
    status: "available",
    // Emailed as a CSV ("Email me this"); the schedule shown in its tooltip.
    emailSchedule: "Sent as a CSV at the start of each business week for the week before.",
    component: PayrollReport,
  },
  {
    id: "log-entries-by-type",
    name: "Log Entries by Type",
    description:
      "Logbook entries for a date range, filterable by Type (logbook category) and search text. (\"Type\" maps to the categories each company configures in their logbook.)",
    group: "ops",
    status: "available",
    // Emailed as a CSV ("Email me this"); the schedule shown in its tooltip.
    emailSchedule: "Sent as a CSV each morning for the day before, with every type.",
    component: LogEntriesByTypeReport,
    filterLabelStyle: "stacked",
    // Only stores with the logbook module on can serve this report —
    // mirrors LogbookReportingDisplay's store filter.
    requiredStoreFlag: "logbook_enabled",
  },
  {
    id: "assistant-questions",
    name: "Assistant Questions",
    description:
      "Internal view of Rovis and Company Files questions, answerability, and likely content or product gaps.",
    group: "ops",
    status: "available",
    internalOnly: true,
    // Filters across companies/stores itself, so the printed header has no
    // single store to name.
    crossStore: true,
    component: AssistantQuestionsReport,
  },

  // ───── Upcoming sales and mix reports ─────
  {
    id: "server-sales",
    name: "Server Sales Report",
    description:
      "Per-server sales across sales departments, comps, promos, paid outs, voids, guest counts, and location averages.",
    group: "sales",
    status: "coming-soon",
  },
  {
    id: "server-pmix",
    name: "Server Pmix Report",
    description:
      "Per-server item sales by product category, including quantity, gross, comp, net, and points.",
    group: "sales",
    status: "coming-soon",
  },
  {
    id: "flash-report-configurable",
    name: "Flash Report (Configurable)",
    description:
      "Selected metrics for a date range, with custom comparison dates per metric.",
    group: "sales",
    status: "coming-soon",
  },
  {
    id: "flash-report",
    name: "Flash Report",
    description:
      "One business day by location: net sales, discounts and comps, voids, checks, guests, average check, and average per guest.",
    group: "sales",
    status: "available",
    // Covers every Toast store the user manages in a company, with its own
    // company/store filters, so the printed header has no single store.
    crossStore: true,
    requiredStoreFlag: "labor_model_enabled",
    requireActiveStore: true,
    requiredPosProvider: "toast",
    component: FlashReport,
  },
  {
    id: "daypart-labor",
    name: "Daypart Labor Report",
    description:
      "Labor dollars, hours, and percentage by daypart and job, with daypart sales and guest counts.",
    group: "labor",
    status: "coming-soon",
  },

  // ───── Upcoming operations and finance reports ─────
  {
    id: "daily-ops-statement",
    name: "Daily Ops Statement",
    description:
      "Daily P&L with sales by category, cost of sales, labor, controllables, and REST.",
    group: "ops",
    status: "coming-soon",
  },
  {
    id: "ops-statement-weekly",
    name: "Ops Statement (Weekly)",
    description:
      "Weekly P&L rollup with budget and prior-year variance by line item.",
    group: "ops",
    status: "coming-soon",
  },
  {
    id: "daily-recap",
    name: "Daily Recap",
    description:
      "Sales by category and daypart, guest metrics, labor by job, and prior-year comparisons.",
    group: "ops",
    status: "coming-soon",
  },
];

export const STATUS_BADGES = {
  available: { label: "Available", className: "bg-emerald-100 text-emerald-700" },
  "coming-soon": {
    label: "Coming soon",
    className: "bg-slate-100 text-slate-600 border border-slate-200",
  },
};

export function getReportById(id) {
  return PREBUILT_REPORTS.find((r) => r.id === id) || null;
}

export function reportVisibleForPermission(report, permissionRole) {
  if (!report) return false;
  if (!report.internalOnly) return true;
  return permissionRole === "rightwork_internal";
}

export { getEligibleStoresForReport, reportRequiresScheduling } from "./reportAccess";
